import { z } from "zod";
import { WorkspaceExpirationFilter } from "@api/shared-runtime/workspace-expiration";
import { PsvProviderStatus } from "@api/shared-runtime/psv-review";

export const WORKSPACE_PAGE_SIZE = 50;
export const WORKSPACE_SEARCH_DELAY_MS = 300;
const MAX_FILTER_VALUES = 100;
export const WORKSPACE_MAX_SEARCH_LENGTH = 200;

export const workspaceSearchSchema = z.object({
  q: z.string().max(WORKSPACE_MAX_SEARCH_LENGTH).optional(),
  employment: z.enum(["hired", "candidate", "all"]).optional(),
  expiration: z.nativeEnum(WorkspaceExpirationFilter).optional(),
  pools: z.array(z.string().uuid()).max(MAX_FILTER_VALUES).optional(),
  specialties: z.array(z.string()).max(MAX_FILTER_VALUES).optional(),
  psv: z.nativeEnum(PsvProviderStatus).optional(),
  offset: z.number().int().nonnegative().optional(),
  /** Legacy drawer links; the route redirects them to the provider page. */
  provider: z.string().uuid().optional(),
});

export type WorkspaceSearch = z.infer<typeof workspaceSearchSchema>;
export type WorkspaceEmployment = "hired" | "candidate" | "all";

export function normalizeWorkspaceFilters(search: WorkspaceSearch) {
  return {
    search: search.q?.trim() || undefined,
    employment: search.employment ?? "hired",
    expiration: search.expiration,
    psvStatus: search.psv,
    providerPoolIds: [...new Set(search.pools ?? [])].sort(),
    specialtyCodes: [...new Set(search.specialties ?? [])].sort(),
    offset: search.offset ?? 0,
    limit: WORKSPACE_PAGE_SIZE,
  };
}

/** Filters and pagination change together so a narrower result never requests
 * the old page before requesting its first page. Explicit All survives reload. */
export function changeWorkspaceFilters({
  previous,
  changes,
}: {
  previous: WorkspaceSearch;
  changes: Partial<WorkspaceSearch>;
}): WorkspaceSearch {
  return { ...previous, ...changes, offset: undefined };
}

export function lastWorkspaceOffset(total: number) {
  return (
    Math.max(0, Math.ceil(total / WORKSPACE_PAGE_SIZE) - 1) *
    WORKSPACE_PAGE_SIZE
  );
}
